/**
 * Play debug panel (tool chrome) — not Play HUD.
 * Telemetry plate and race status live in telemetryStrip.tsx / raceHud.tsx.
 * See docs/PLAY-HUD.md.
 */
import { Fragment, type ComponentChildren, type JSX } from 'preact';
import { memo } from 'preact/compat';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  AUTO_CAMERA_HINT,
  CAMERA_READOUT_HINTS,
  DEFAULT_PLAY_CAMERA,
  PLAY_CAMERA_SLIDERS,
  formatPlayCameraLimit,
  formatUserZoomFactor,
  formatZoom,
  type PlayCameraLimit,
  type PlayCameraSettings,
  type PlayCameraSliderKey,
} from '../../sim/debugDefaults/index.ts';
import { RING_MODES, type RingMode } from '../../sim/replay.ts';
import {
  CRASH_HARSHNESS,
  CRASH_HARSHNESS_MAX,
  CRASH_HARSHNESS_MIN,
  CRASH_HARSHNESS_STEP,
  STICK_OVERFLOW_PX,
  TURN_TAX,
  TURN_TAX_MAX,
  TURN_TAX_MIN,
  TURN_TAX_STEP,
} from '../../sim/constants.ts';
import {
  DEFAULT_WIND_FEEL,
  WIND_COEFFICIENT_MAX,
  WIND_COEFFICIENT_MIN,
  WIND_COEFFICIENT_STEP,
  WIND_EXPOSURE_MAX,
  WIND_EXPOSURE_MIN,
  WIND_EXPOSURE_STEP,
  WIND_LOW_SPEED_MAX,
  WIND_LOW_SPEED_MIN,
  WIND_LOW_SPEED_STEP,
  WIND_OFFSET_CAP_MAX_M,
  WIND_OFFSET_CAP_MIN_M,
  WIND_OFFSET_CAP_STEP_M,
  type WindFeel,
} from '../../sim/environment/wind.ts';
import { NEAR_MISS_PX } from '../../sim/race.ts';
import {
  DEFAULT_CAP_FLOCK_AMOUNT,
  DEFAULT_CAP_FLOCK_ENABLED,
  DEFAULT_SUN,
  DEFAULT_UNDER_CAP_RIM_ALPHA,
  DEFAULT_WATERLINE_HINT_ALPHA,
  cloneAimChrome,
  createDefaultLook,
  DEFAULT_AIM_CHROME,
  type AimChrome,
  type MapLook,
  type SunSettings,
} from '../../sim/map/index.ts';
import type { PlayLogLine } from '../../sim/damage.ts';
import type { CurveKind } from '../../sim/path.ts';
import { CURVE_KINDS, DEFAULT_CURVE_KIND } from '../../sim/path.ts';
import { competitorTintCss } from '../../game/competitorTint.ts';
import { cloneRgba } from '../../sim/color.ts';
import { ColorPicker, LiveSlider, Readout, Select, Slider, Stepper, Toggle, ToggleStepper } from '../controls.tsx';
import { LightingLookGroup } from '../lookSettings.tsx';
import { setPanel } from '../panels.ts';
import type { SaveLookToMapResult } from './saveLookToMap.ts';
import {
  EMPTY_PERF_READOUT,
  formatMs,
  formatUploadBytes,
  type PerfReadout,
} from './perfReadout.ts';
import './debugPanel.css';

export type DebugLines = {
  trail: boolean;
  moveLine: boolean;
  prediction: boolean;
  windGhost: boolean;
};

export type PlayDebugState = {
  radiusMeters: number;
  /** Test boat with an unequal reach. Circle size cannot override it. */
  radiusLocked: boolean;
  crashHarshness: number;
  turnTax: number;
  ringMode: RingMode;
  trailLengthMeters: number;
  trailSpeedGain: number;
  trailKeptMeters: number;
  curve: CurveKind;
  lines: DebugLines;
  windFeel: WindFeel;
  capFlockEnabled: boolean;
  capFlockAmount: number;
  waterlineHintAlpha: number;
  underCapRimAlpha: number;
  sun: SunSettings;
  look: MapLook;
  showRoute: boolean;
  previewGates: boolean;
  logLines: PlayLogLine[];
  stickyEdges: boolean;
  stickyOverflowPx: number;
  nearMissPx: number;
  camera: PlayCameraSettings;
  fitZoom: number;
  speedZoom: number;
  autoZoom: number;
  cameraLimit: PlayCameraLimit;
  userZoomFactor: number;
  viewZoom: number;
  aimChrome: AimChrome;
  /** Render probe (docs/PERFORMANCE.md). Refreshed a few times a second, not per frame. */
  perf: PerfReadout;
};

export type PlayDebugActions = {
  setCurve: (kind: CurveKind) => void;
  setLine: (line: keyof DebugLines, next: boolean) => void;
  setWindFeel: (patch: Partial<WindFeel>) => void;
  setCapFlockEnabled: (next: boolean) => void;
  setCapFlockAmount: (next: number) => void;
  onSunLive: (sun: Partial<SunSettings>) => void;
  onSunCommit: () => void;
  onLookLive: (patch: Partial<MapLook>) => void;
  onLookCommit: () => void;
  setShowRoute: (next: boolean) => void;
  setPreviewGates: (next: boolean) => void;
  setStickyEdges: (next: boolean) => void;
  stepStickyOverflow: (direction: -1 | 1) => void;
  setStickyOverflow: (next: number) => void;
  stepNearMiss: (direction: -1 | 1) => void;
  setNearMiss: (next: number) => void;
  stepRadius: (direction: -1 | 1) => void;
  setCrashHarshness: (next: number) => void;
  setTurnTax: (next: number) => void;
  setRingMode: (next: RingMode) => void;
  stepTrailLength: (direction: -1 | 1) => void;
  stepTrailSpeedGain: (direction: -1 | 1) => void;
  setCameraEnabled: (next: boolean) => void;
  setCameraSlider: (key: PlayCameraSliderKey, next: number) => void;
  onAimChromeLive: (patch: Partial<AimChrome>) => void;
  setWaterlineHintAlpha: (next: number) => void;
  setUnderCapRimAlpha: (next: number) => void;
  saveToMap: () => Promise<SaveLookToMapResult>;
};

export type PlayDebugProps = {
  state: PlayDebugState;
  actions: PlayDebugActions;
  /** `?perf`: start with Debug expanded on the Perf section. */
  perfOpen?: boolean;
};

export type PlayDebug = {
  sync: (state: PlayDebugState) => void;
  destroy: () => void;
};

const INITIAL_STATE: PlayDebugState = {
  radiusMeters: 0,
  radiusLocked: false,
  crashHarshness: CRASH_HARSHNESS,
  turnTax: TURN_TAX,
  ringMode: 'off',
  trailLengthMeters: 0,
  trailSpeedGain: 0,
  trailKeptMeters: 0,
  curve: DEFAULT_CURVE_KIND,
  lines: { trail: true, moveLine: true, prediction: true, windGhost: false },
  windFeel: { ...DEFAULT_WIND_FEEL },
  capFlockEnabled: DEFAULT_CAP_FLOCK_ENABLED,
  capFlockAmount: DEFAULT_CAP_FLOCK_AMOUNT,
  waterlineHintAlpha: DEFAULT_WATERLINE_HINT_ALPHA,
  underCapRimAlpha: DEFAULT_UNDER_CAP_RIM_ALPHA,
  sun: { ...DEFAULT_SUN },
  look: createDefaultLook(),
  showRoute: false,
  previewGates: true,
  logLines: [],
  stickyEdges: true,
  stickyOverflowPx: STICK_OVERFLOW_PX,
  nearMissPx: NEAR_MISS_PX,
  camera: { ...DEFAULT_PLAY_CAMERA },
  fitZoom: 1,
  speedZoom: 1,
  autoZoom: 1,
  cameraLimit: 'Speed',
  userZoomFactor: 1,
  viewZoom: 1,
  aimChrome: cloneAimChrome(DEFAULT_AIM_CHROME),
  perf: EMPTY_PERF_READOUT,
};

export const DEFAULT_DEBUG_LINES: DebugLines = INITIAL_STATE.lines;
export const DEFAULT_PREVIEW_GATES = INITIAL_STATE.previewGates;
export const DEFAULT_STICKY_EDGES = INITIAL_STATE.stickyEdges;

type DebugSectionId =
  | 'movement'
  | 'wind'
  | 'overlays'
  | 'look'
  | 'chrome'
  | 'trail'
  | 'camera'
  | 'perf'
  | 'log';

const INITIAL_SECTIONS: Record<DebugSectionId, boolean> = {
  movement: false,
  wind: false,
  overlays: false,
  look: false,
  chrome: false,
  trail: false,
  camera: false,
  perf: false,
  log: true,
};

function DebugSection({
  id,
  title,
  open,
  onToggle,
  children,
}: {
  id: DebugSectionId;
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ComponentChildren;
}): JSX.Element {
  const bodyId = `play-debug-section-${id}`;

  return (
    <section class="play-debug-section">
      <button
        type="button"
        class="play-debug-section-toggle"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        {title}
        <span class="editor-dock-chevron" aria-hidden="true">
          ▾
        </span>
      </button>
      <div id={bodyId} class="play-debug-section-body" hidden={!open}>
        {children}
      </div>
    </section>
  );
}

export const PlayDebugPanel = memo(function PlayDebugPanel({
  state,
  actions,
  perfOpen = false,
}: PlayDebugProps): JSX.Element {
  const [debugOpen, setDebugOpen] = useState(perfOpen);
  const [openSections, setOpenSections] = useState(() =>
    perfOpen ? { ...INITIAL_SECTIONS, perf: true, log: false } : INITIAL_SECTIONS,
  );
  const [saveStatus, setSaveStatus] = useState<'idle' | SaveLookToMapResult>('idle');
  const logBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const body = logBodyRef.current;

    if (body) {
      body.scrollTop = body.scrollHeight;
    }
  }, [state.logLines]);

  const toggleSection = (id: DebugSectionId): void => {
    setOpenSections((current) => ({ ...current, [id]: !current[id] }));
  };

  const saveStatusLabel = (status: SaveLookToMapResult): string => {
    switch (status) {
      case 'saved':
        return 'Saved to map';
      case 'applied':
        return 'Applied to editor map';
      case 'dev-only':
        return 'Dev only';
      case 'cancelled':
        return 'Cancelled';
      case 'no-target':
        return 'No bound map';
      default:
        return 'Could not save';
    }
  };

  return (
    <aside class="play-debug ui-panel" aria-label="Debug">
      <div class="play-debug-group ui-chrome">
        <div class="play-debug-toolbar">
          <button
            type="button"
            class="ui-button play-debug-debug-toggle"
            aria-expanded={debugOpen ? 'true' : 'false'}
            aria-controls="play-debug-debug"
            onClick={() => setDebugOpen(!debugOpen)}
          >
            Debug
          </button>
          {debugOpen ? (
            <button
              type="button"
              class="ui-button"
              onClick={() => {
                void actions.saveToMap().then((result) => {
                  setSaveStatus(result);
                });
              }}
            >
              Save to Map
            </button>
          ) : null}
        </div>
        {saveStatus !== 'idle' && debugOpen ? (
          <p class="ui-hint play-debug-save-status" role="status">
            {saveStatusLabel(saveStatus)}
          </p>
        ) : null}
        <div id="play-debug-debug" class="play-debug-debug" hidden={!debugOpen}>
          <DebugSection
            id="movement"
            title="Movement"
            open={openSections.movement}
            onToggle={() => toggleSection('movement')}
          >
            <Select
              label="Curve"
              value={state.curve}
              options={CURVE_KINDS.map(({ kind, label }) => ({
                value: kind,
                label,
              }))}
              onChange={actions.setCurve}
            />
            <Stepper
              label="Circle size"
              value={`${state.radiusMeters.toFixed(1)} m`}
              disabled={state.radiusLocked}
              onStep={actions.stepRadius}
            />
            <Slider
              label="Crash harshness"
              value={state.crashHarshness}
              min={CRASH_HARSHNESS_MIN}
              max={CRASH_HARSHNESS_MAX}
              step={CRASH_HARSHNESS_STEP}
              display={`${state.crashHarshness.toFixed(1)}×`}
              onChange={actions.setCrashHarshness}
            />
            <Slider
              label="Turn tax"
              value={state.turnTax}
              min={TURN_TAX_MIN}
              max={TURN_TAX_MAX}
              step={TURN_TAX_STEP}
              display={`${Math.round(state.turnTax * 100)}%`}
              onChange={actions.setTurnTax}
            />
            <div class="ui-control" title="P cycles Off, Planing, and Headreach.">
              <span class="ui-control-label">Ring</span>
              <div class="ui-button-group" role="radiogroup" aria-label="Ring">
                {RING_MODES.map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    class="ui-button ui-toggle"
                    aria-pressed={state.ringMode === mode ? 'true' : 'false'}
                    onClick={() => actions.setRingMode(mode)}
                  >
                    {mode === 'off' ? 'Off' : mode === 'planing' ? 'Planing' : 'Headreach'}
                  </button>
                ))}
              </div>
            </div>
          </DebugSection>
          <DebugSection
            id="wind"
            title="Wind"
            open={openSections.wind}
            onToggle={() => toggleSection('wind')}
          >
            <Toggle
              label="Wind effect"
              value={state.windFeel.enabled}
              onChange={(enabled) => actions.setWindFeel({ enabled })}
              hint="Session A/B. Off forces zero disk offset without changing the map."
            />
            <Slider
              label="Coefficient"
              value={state.windFeel.coefficient}
              min={WIND_COEFFICIENT_MIN}
              max={WIND_COEFFICIENT_MAX}
              step={WIND_COEFFICIENT_STEP}
              display={state.windFeel.coefficient.toFixed(3)}
              onChange={(coefficient) => actions.setWindFeel({ coefficient })}
            />
            <Slider
              label="Exposure"
              value={state.windFeel.exposure}
              min={WIND_EXPOSURE_MIN}
              max={WIND_EXPOSURE_MAX}
              step={WIND_EXPOSURE_STEP}
              display={`${state.windFeel.exposure.toFixed(1)}×`}
              onChange={(exposure) => actions.setWindFeel({ exposure })}
            />
            <Slider
              label="Low-speed multiplier"
              value={state.windFeel.lowSpeedMultiplier}
              min={WIND_LOW_SPEED_MIN}
              max={WIND_LOW_SPEED_MAX}
              step={WIND_LOW_SPEED_STEP}
              display={`${state.windFeel.lowSpeedMultiplier.toFixed(1)}×`}
              onChange={(lowSpeedMultiplier) => actions.setWindFeel({ lowSpeedMultiplier })}
            />
            <Slider
              label="Offset cap"
              value={state.windFeel.offsetCapM}
              min={WIND_OFFSET_CAP_MIN_M}
              max={WIND_OFFSET_CAP_MAX_M}
              step={WIND_OFFSET_CAP_STEP_M}
              display={`${state.windFeel.offsetCapM.toFixed(1)} m`}
              onChange={(offsetCapM) => actions.setWindFeel({ offsetCapM })}
            />
          </DebugSection>
          <DebugSection
            id="overlays"
            title="Overlays"
            open={openSections.overlays}
            onToggle={() => toggleSection('overlays')}
          >
            <Toggle
              label="Trail line"
              value={state.lines.trail}
              onChange={(next) => actions.setLine('trail', next)}
            />
            <Toggle
              label="Move line"
              value={state.lines.moveLine}
              onChange={(next) => actions.setLine('moveLine', next)}
            />
            <Toggle
              label="Gate preview"
              value={state.previewGates}
              onChange={actions.setPreviewGates}
            />
            <ToggleStepper
              label="Sticky edges"
              toggleValue={state.stickyEdges}
              onToggle={actions.setStickyEdges}
              value={state.stickyOverflowPx}
              unit="px"
              onStep={actions.stepStickyOverflow}
              onValueCommit={actions.setStickyOverflow}
            />
            <Stepper
              label="Gate miss"
              value={`${Math.round(state.nearMissPx)} px`}
              onStep={actions.stepNearMiss}
            />
            <Toggle
              label="Prediction"
              value={state.lines.prediction}
              onChange={(next) => actions.setLine('prediction', next)}
            />
            <Toggle
              label="Wind ghost"
              value={state.lines.windGhost}
              onChange={(next) => actions.setLine('windGhost', next)}
              hint="Provisional debug. No-wind circle. Not a target."
            />
            <Toggle
              label="Show route"
              value={state.showRoute}
              onChange={actions.setShowRoute}
            />
          </DebugSection>
          <DebugSection
            id="look"
            title="Look"
            open={openSections.look}
            onToggle={() => toggleSection('look')}
          >
            <Toggle
              label="Cap flock"
              value={state.capFlockEnabled}
              onChange={actions.setCapFlockEnabled}
              hint="Organic paint on extruded island and rock decks"
            />
            {state.capFlockEnabled ? (
              <LiveSlider
                label="Cap flock"
                minLabel="Less"
                maxLabel="More"
                value={state.capFlockAmount}
                dataAttr="data-cap-flock-amount"
                hint="How many ovals sit on each extruded deck"
                onInput={actions.setCapFlockAmount}
                onCommit={() => undefined}
              />
            ) : null}
            <LiveSlider
              label="Waterline hint"
              value={state.waterlineHintAlpha}
              dataAttr="data-waterline-hint-alpha"
              onInput={actions.setWaterlineHintAlpha}
              onCommit={() => undefined}
            />
            <LiveSlider
              label="Under-cap rim"
              value={state.underCapRimAlpha}
              dataAttr="data-under-cap-rim-alpha"
              hint="Dashed movement and prediction rims on legal water hidden by a north cap"
              onInput={actions.setUnderCapRimAlpha}
              onCommit={() => undefined}
            />
            <LightingLookGroup
              sun={state.sun}
              look={state.look}
              onSunLive={actions.onSunLive}
              onSunCommit={actions.onSunCommit}
              onLookLive={actions.onLookLive}
              onLookCommit={actions.onLookCommit}
            />
          </DebugSection>
          <DebugSection
            id="chrome"
            title="Chrome"
            open={openSections.chrome}
            onToggle={() => toggleSection('chrome')}
          >
            {(
              [
                ['movement', 'Movement circle', 'Movement alpha', 'data-movement-alpha'],
                ['prediction', 'Prediction circle', 'Prediction alpha', 'data-prediction-alpha'],
                ['moveLine', 'Move line', 'Move line alpha', 'data-move-line-alpha'],
                ['dest', 'Dest', 'Dest alpha', 'data-dest-alpha'],
              ] as const
            ).map(([key, colorLabel, alphaLabel, dataAttr]) => {
              const value = state.aimChrome[key];

              return (
                <Fragment key={key}>
                  <ColorPicker
                    label={colorLabel}
                    value={value}
                    onChange={(next) =>
                      actions.onAimChromeLive({ [key]: next })
                    }
                  />
                  <LiveSlider
                    label={alphaLabel}
                    value={value.a}
                    dataAttr={dataAttr}
                    onInput={(a) =>
                      actions.onAimChromeLive({
                        [key]: cloneRgba({ ...value, a }),
                      })
                    }
                    onCommit={() => undefined}
                  />
                </Fragment>
              );
            })}
          </DebugSection>
          <DebugSection
            id="trail"
            title="Trail"
            open={openSections.trail}
            onToggle={() => toggleSection('trail')}
          >
            <Stepper
              label="Trail length"
              value={`${state.trailLengthMeters.toFixed(0)} m`}
              onStep={actions.stepTrailLength}
            />
            <Stepper
              label="Speed gain"
              value={`${state.trailSpeedGain.toFixed(1)}×`}
              onStep={actions.stepTrailSpeedGain}
            />
            <Readout
              label="Kept"
              value={`${Math.round(state.trailKeptMeters)} m`}
            />
          </DebugSection>
          <DebugSection
            id="camera"
            title="Camera"
            open={openSections.camera}
            onToggle={() => toggleSection('camera')}
          >
            <Toggle
              label="Auto camera"
              value={state.camera.enabled}
              hint={AUTO_CAMERA_HINT}
              onChange={actions.setCameraEnabled}
            />
            {(Object.keys(PLAY_CAMERA_SLIDERS) as PlayCameraSliderKey[]).map(
              (key) => {
                const spec = PLAY_CAMERA_SLIDERS[key];

                return (
                  <Slider
                    key={key}
                    label={spec.label}
                    value={state.camera[key]}
                    min={spec.min}
                    max={spec.max}
                    step={spec.step}
                    display={spec.format(state.camera[key])}
                    hint={spec.hint}
                    onChange={(next) => actions.setCameraSlider(key, next)}
                  />
                );
              },
            )}
            <Readout
              label="Fit"
              value={formatZoom(state.fitZoom)}
              hint={CAMERA_READOUT_HINTS.fit}
            />
            <Readout
              label="Speed z"
              value={formatZoom(state.speedZoom)}
              hint={CAMERA_READOUT_HINTS.speed}
            />
            <Readout
              label="Auto"
              value={formatZoom(state.autoZoom)}
              hint={CAMERA_READOUT_HINTS.auto}
            />
            <Readout
              label="Limit"
              value={formatPlayCameraLimit(state.cameraLimit)}
              hint={CAMERA_READOUT_HINTS.limit}
            />
            <Readout
              label="User"
              value={formatUserZoomFactor(state.userZoomFactor)}
              hint={CAMERA_READOUT_HINTS.user}
            />
            <Readout
              label="View"
              value={formatZoom(state.viewZoom)}
              hint={CAMERA_READOUT_HINTS.view}
            />
          </DebugSection>
          <DebugSection
            id="perf"
            title="Perf"
            open={openSections.perf}
            onToggle={() => toggleSection('perf')}
          >
            <div class="play-perf" data-perf-frames={state.perf.frames}>
              <Readout
                label="FPS"
                value={String(state.perf.fps)}
                hint="Phaser loop actualFps"
              />
              <Readout
                label="JS"
                value={`${formatMs(state.perf.jsMs)} · p90 ${formatMs(state.perf.jsP90Ms)}`}
                hint="Main thread per frame: update + render + GL submission (median over ~90 frames)"
              />
              <Readout
                label="GPU"
                value={
                  state.perf.gpuMs === null
                    ? 'no timer ext'
                    : `${formatMs(state.perf.gpuMs)} · p90 ${formatMs(state.perf.gpuP90Ms)}`
                }
                hint="EXT_disjoint_timer_query per frame"
              />
              <Readout
                label="Draws"
                value={`${state.perf.drawCalls} · ${state.perf.triangles.toLocaleString()} tris`}
                hint="drawElements calls and triangles submitted last frame"
              />
              <Readout
                label="Upload"
                value={formatUploadBytes(state.perf.uploadBytes)}
                hint="bufferSubData / bufferData bytes last frame"
              />
              <Readout
                label="FBO binds"
                value={String(state.perf.fboBinds)}
                hint="Framebuffer binds last frame (filters and dynamic textures)"
              />
            </div>
          </DebugSection>
          <DebugSection
            id="log"
            title="Log"
            open={openSections.log}
            onToggle={() => toggleSection('log')}
          >
            <div class="play-log-body" ref={logBodyRef}>
              {state.logLines.length === 0 ? (
                <p class="play-log-empty">No contacts yet.</p>
              ) : (
                <ol class="play-log-lines">
                  {state.logLines.map((line, index) => (
                    <li
                      key={`${index}:${line.seatId}:${line.text}`}
                      class="play-log-line"
                      aria-label={`${line.label} · ${line.text}`}
                    >
                      <span
                        class="play-log-seat-dot"
                        style={{ background: competitorTintCss(line.seatId) }}
                        data-color={competitorTintCss(line.seatId)}
                        aria-hidden="true"
                      />
                      <span class="play-log-text">
                        {line.label} · {line.text}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </DebugSection>
        </div>
      </div>
    </aside>
  );
});

export function mountPlayDebug(
  actions: PlayDebugActions,
  options: { perfOpen?: boolean } = {},
): PlayDebug {
  const perfOpen = options.perfOpen === true;
  setPanel('playDebug', { state: INITIAL_STATE, actions, perfOpen });

  return {
    sync: (state) => setPanel('playDebug', { state, actions, perfOpen }),
    destroy: () => setPanel('playDebug', null),
  };
}
